import { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { PreferenceActionPayload, UserPreferencesProps } from './UserPreferences.types';
import Layout from '../../../common/Layout/Layout';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import HeadingWithIcon from '../../../common/HeadingWithIcon/HeadingWithIcon';
import ContentBlockView from '../../../common/ContentBlockView/ContentBlockView';
import Checkbox from '../../../common/Checkbox/Checkbox';
import { Role, useRole } from '../../../../lib/context/SessionContext';
import PureHeading from '../../../common/Heading/PureHeading';
import { classes } from '../../../../lib/utils/cssUtils';
import { usePreferences, useUserPreferences } from '../../../../lib/api/swrApi';
import { useViewContext } from '../../../../lib/context/ViewContext';
import { useDeleteUserPreference, useSetUserPreference } from '../../../../lib/api/postApi';
import { Api } from '../../../../lib/types/apiSpec';
import useDeleteState from '../../../../lib/hooks/useDeleteState';
import { equals, filter, notEquals, notIncludes } from '../../../../lib/utils/functional';
import { LeftArrow } from '../../../../lib/icons/LeftArrow';
import { Label } from '../../../common/Label/Label';
import { FILTER_LABELS, USER_PREFERENCES_LABELS } from '../../../../lib/constants/labelKeys';
import { UserPreferenceSkeleton } from './UserPreferencesSkeleton';
import styles from './UserPreferences.module.scss';
import { userPreferencesIds } from '../../../../lib/tests/testIds';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';
import ErrorBoundary from '../../../common/ErrorBoundary/ErrorBoundary';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

const updateStateFn = (type: 'set' | 'delete', id: string) => (prev: string[]) =>
  type === 'set' ? [...prev, id] : prev.filter(notEquals(id));

const UserPreferences = ({
  heading,
  headingLevel,
  icon,
  introMessage,
  disciplinesHeading,
  interestAreasHeading,
}: UserPreferencesProps) => {
  const role = useRole();
  const { data: preferences, isLoading: isPreferencesLoading, error: preferencesError } = usePreferences();
  const { app } = useViewContext();
  const {
    data: userPreferences,
    isLoading: isUserPreferencesLoading,
    error: userPreferencesError,
    mutate: mutateUserPreferences,
  } = useUserPreferences({
    userid: app.user?.userId ?? '',
    shouldFetch: !!app.user?.userId,
  });

  const interestAreasRefs = useRef<Record<string, HTMLDivElement>>({});

  const setUserPreference = useSetUserPreference(app.user?.userId ?? '');
  const deleteUserPreference = useDeleteUserPreference(app.user?.userId ?? '');

  const { triggerSnackbarError } = useErrorMapping();

  const [elementsProcessing, dispatchElementProcessing] = useDeleteState({});
  const [selectedDisciplines, setSelectedDisciplines] = useState<string[]>([]);
  const [selectedInterestAreas, setSelectedInterestAreas] = useState<string[]>([]);
  const [newDisciplines, setNewDisciplines] = useState<string[]>([]);

  const isLoading = isPreferencesLoading || isUserPreferencesLoading;

  const action = async ({ type, ...params }: PreferenceActionPayload) => {
    dispatchElementProcessing({ type: 'deleteStateAdd', payload: params.id });

    const updateState = (prefType: Api.Preference) =>
      prefType === 'interestAreas' ? setSelectedInterestAreas : setSelectedDisciplines;

    updateState(params.preferenceType)(updateStateFn(type, params.id));

    if (params.preferenceType === 'disciplines') {
      setNewDisciplines(updateStateFn(type, params.id));
    }

    if (type === 'delete' && params.preferenceType === 'disciplines') {
      const relatedInterestAreas = preferences?.disciplines
        .find(discipline => discipline.id === params.id)
        ?.interestAreas.map(({ id }) => id);
      setSelectedInterestAreas(filter(notIncludes(relatedInterestAreas ?? [])));
    }

    try {
      if (type === 'delete') {
        await deleteUserPreference(params);
      } else {
        await setUserPreference(params);
      }
      mutateUserPreferences();
    } catch (error: any) {
      updateState(params.preferenceType)(updateStateFn(type === 'set' ? 'delete' : 'set', params.id));
      if (type === 'set' && params.preferenceType === 'disciplines') {
        setNewDisciplines(filter(notEquals(params.id)));
      }

      triggerSnackbarError(error);
    } finally {
      dispatchElementProcessing({ type: 'deleteStateRemove', payload: params.id });
    }
  };

  const handleScrollToNew = () => {
    const lastElement = newDisciplines.at(-1);

    if (lastElement) {
      interestAreasRefs.current?.[lastElement].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });

      setNewDisciplines(filter(notEquals(lastElement)));
    }
  };

  useEffect(() => {
    const observer: { current: IntersectionObserver | null } = { current: null };

    if (newDisciplines.length) {
      observer.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            observer.current?.disconnect();
            setNewDisciplines(filter(notEquals(entry.target.id)));
          }
        },
        {
          threshold: 1,
        },
      );

      newDisciplines.forEach(discipline => {
        observer.current?.observe(interestAreasRefs.current[discipline]);
      });
    } else {
      observer.current?.disconnect();
    }

    return () => {
      observer.current?.disconnect();
    };
  }, [newDisciplines]);

  useEffect(() => {
    if (preferences && userPreferences) {
      setSelectedDisciplines(userPreferences.disciplines);
      setSelectedInterestAreas(userPreferences.interestAreas);
    }
  }, [preferences, userPreferences]);

  return (
    <Layout>
      <button
        aria-live="polite"
        className={classes(styles.newInterestAreasInfo, {
          [styles.hiddenNewInterestAreasInfo]: !newDisciplines.length,
        })}
        onClick={handleScrollToNew}
      >
        <Label name={USER_PREFERENCES_LABELS.NEW_INTEREST_AREAS_MESSAGE} />
        <LeftArrow className={styles.infoArrow} />
      </button>
      <HeadingWithIcon level={HeadingLevel.h2} as={headingLevel} icon={icon} classNames={{ container: styles.heading }}>
        {heading}
      </HeadingWithIcon>
      {!!introMessage && (
        <div className={styles.introMessage}>
          <ContentBlockView contentBlock={introMessage} />
        </div>
      )}
      <ErrorBoundary error={userPreferencesError ?? preferencesError} internal>
        <div role="form" className={styles.formSection}>
          {role === Role.LEARNER_MANAGER && (
            <section className={styles.preferenceSection} aria-labelledby={userPreferencesIds.disciplinesHeading}>
              <PureHeading level={HeadingLevel.h3} id={userPreferencesIds.disciplinesHeading}>
                {disciplinesHeading}
              </PureHeading>
              <UserPreferenceSkeleton isLoading={isLoading}>
                <div className={styles.checkboxesSection}>
                  {preferences?.disciplines?.map(({ id, name }) => {
                    const selected = !!selectedDisciplines.find(equals(id));
                    const onlySelected = selectedDisciplines.length === 1 && selected;
                    const isDisciplineLoading = elementsProcessing[id];

                    return (
                      <Tooltip
                        key={id}
                        title={onlySelected && <Label name={USER_PREFERENCES_LABELS.LAST_DISCIPLINE_TOOLTIP} />}
                        classes={{ tooltip: styles.tooltip }}
                        placement="top"
                        arrow
                      >
                        <Checkbox
                          className={styles.checkbox}
                          variant="primary"
                          aria-live="polite"
                          isLoading={isDisciplineLoading}
                          disabled={onlySelected || isDisciplineLoading}
                          label={
                            <Label name={FILTER_LABELS.FACET_VALUE(Api.FacetName.disciplines, id)} fallback={name} />
                          }
                          selected={selected}
                          onSelect={() =>
                            action({
                              id,
                              type: selected ? 'delete' : 'set',
                              preferenceType: 'disciplines',
                            })
                          }
                        />
                      </Tooltip>
                    );
                  })}
                </div>
              </UserPreferenceSkeleton>
            </section>
          )}
          <section aria-labelledby={userPreferencesIds.interestAreasHeading} className={styles.preferenceSection}>
            <PureHeading level={HeadingLevel.h3} id={userPreferencesIds.interestAreasHeading}>
              {interestAreasHeading}
            </PureHeading>
            <UserPreferenceSkeleton withSubtitle isLoading={isLoading}>
              {preferences?.disciplines
                .filter(d => selectedDisciplines.includes(d.id))
                .map(discipline => (
                  <div
                    ref={el => {
                      if (el) {
                        interestAreasRefs.current[discipline.id] = el;
                      }
                    }}
                    id={discipline.id}
                    key={`${discipline.id}-interest-areas`}
                    role="group"
                    aria-labelledby={`${discipline.id}-interest-areas`}
                    className={styles.interestAreaSection}
                  >
                    {elementsProcessing[discipline.id] && <RefreshLoader />}
                    <PureHeading
                      level={HeadingLevel.h4}
                      className={styles.interestAreaHeading}
                      id={`${discipline.id}-interest-areas`}
                    >
                      <Label
                        name={FILTER_LABELS.FACET_VALUE(Api.FacetName.disciplines, discipline.id)}
                        fallback={discipline.name}
                      />
                    </PureHeading>
                    <div className={styles.checkboxesSection}>
                      {discipline.interestAreas.map(({ id, name }) => {
                        const selected = !!selectedInterestAreas.find(userInterestArea => userInterestArea === id);

                        return (
                          <Checkbox
                            variant="primary"
                            aria-live="polite"
                            isLoading={elementsProcessing[id]}
                            className={styles.checkbox}
                            key={id}
                            label={
                              <Label
                                name={FILTER_LABELS.FACET_VALUE(Api.FacetName.interestAreas, id)}
                                fallback={name}
                              />
                            }
                            selected={selected}
                            onSelect={() =>
                              action({
                                id,
                                type: selected ? 'delete' : 'set',
                                preferenceType: 'interestAreas',
                              })
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
            </UserPreferenceSkeleton>
          </section>
        </div>
      </ErrorBoundary>
    </Layout>
  );
};

export default UserPreferences;
